import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSync } from "../context/SyncContext";
import { useTheme } from "../context/ThemeContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ROLES } from "../lib/format";
import {
  LayoutDashboard, Briefcase, Users, TrendingUp, CreditCard, Receipt,
  FileText, Printer, UserCheck, ShieldCheck, Search, LogOut, ChevronDown,
  SettingsIcon, CheckSquare, Menu, X, Camera, ClipboardCheck, FileSpreadsheet, Sun, Moon, MessageSquare, FileSignature, ScrollText, Home, Wallet, Hammer, Smartphone, Megaphone, CalendarDays, HardHat, GraduationCap,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import NotificationBell from "./NotificationBell";
import CompanySwitcher from "./CompanySwitcher";

function SidebarOrg() {
  const [org, setOrg] = useState({});
  useEffect(() => { api.get("/organization-info").then(({ data }) => setOrg(data || {})).catch(() => {}); }, []);
  return (
    <div className="px-4 py-4 border-t border-slate-800/70 text-[11px] text-slate-500" data-testid="sidebar-org">
      SilasTech Enterprise · Cameroon
      {(org.reg_no || org.email || org.phone) && <div className="mt-1 text-slate-600" data-testid="sidebar-reg-no">{[org.reg_no && `Reg. No ${org.reg_no}`, org.email, org.phone].filter(Boolean).join(" · ")}</div>}
    </div>
  );
}

const NAV_GROUPS = [
  { label: "Overview", items: [
    { name: "Dashboard", icon: LayoutDashboard, path: "/dashboard", roles: [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO, ROLES.SS, ROLES.EMPLOYEE, ROLES.INTERN] },
    { name: "Welcome", icon: Home, path: "/welcome", roles: [ROLES.VISITOR] },
    { name: "Approvals", icon: ClipboardCheck, path: "/approvals", roles: [ROLES.CEO, ROLES.ACEO] },
    { name: "Announcements", icon: Megaphone, path: "/announcements", roles: [ROLES.CEO, ROLES.ACEO, ROLES.GM, ROLES.FM] },
  ]},
  { label: "Projects & Work", items: [
    { name: "Projects", icon: Briefcase, path: "/projects", roles: [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO, ROLES.EMPLOYEE] },
    { name: "Additional Works", icon: Hammer, path: "/additional-works", roles: [ROLES.CEO, ROLES.GM, ROLES.ACEO] },
    { name: "Sub Contracts", icon: FileSignature, path: "/sub-contracts", roles: [ROLES.CEO, ROLES.ACEO, ROLES.GM, ROLES.FM] },
    { name: "Work Program", icon: CalendarDays, path: "/work-program", roles: [ROLES.CEO] },
    { name: "My Schedule", icon: CalendarDays, path: "/my-schedule", roles: [ROLES.EMPLOYEE, ROLES.INTERN] },
    { name: "Solar Calculator", icon: Sun, path: "/solar-calculator", roles: [ROLES.CEO, ROLES.GM, ROLES.ACEO, ROLES.FM] },
  ]},
  { label: "People", items: [
    { name: "Employees", icon: Users, path: "/employees", roles: [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO, ROLES.SS] },
    { name: "Staff", icon: Users, path: "/staff", roles: [ROLES.CEO, ROLES.FM] },
    { name: "Contractors", icon: HardHat, path: "/contractors", roles: [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO] },
    { name: "Students", icon: GraduationCap, path: "/students", roles: [ROLES.CEO, ROLES.ACEO, ROLES.GM, ROLES.SS, ROLES.INTERN] },
    { name: "Salaries", icon: Wallet, path: "/salaries", roles: [ROLES.CEO, ROLES.FM] },
  ]},
  { label: "Finance", items: [
    { name: "Income", icon: TrendingUp, path: "/income", roles: [ROLES.CEO, ROLES.FM, ROLES.ACEO] },
    { name: "Receipts", icon: Receipt, path: "/receipts", roles: [ROLES.CEO, ROLES.FM] },
    { name: "Payments", icon: CreditCard, path: "/payments", roles: [ROLES.CEO, ROLES.FM, ROLES.ACEO] },
    { name: "Payment Checks", icon: CheckSquare, path: "/checks", roles: [ROLES.CEO, ROLES.FM, ROLES.ACEO] },
    { name: "Mobile Money", icon: Smartphone, path: "/mobile-money", roles: [ROLES.FM] },
    { name: "Expenditures", icon: Receipt, path: "/expenditures", roles: [ROLES.CEO, ROLES.FM, ROLES.ACEO] },
    { name: "Financial Statements", icon: FileText, path: "/financial-statements", roles: [ROLES.CEO, ROLES.FM, ROLES.ACEO] },
    { name: "Requests", icon: FileSpreadsheet, path: "/requests", roles: [ROLES.CEO, ROLES.FM, ROLES.ACEO, ROLES.GM] },
  ]},
  { label: "Documents", items: [
    { name: "Reports & Receipts", icon: Printer, path: "/reports", roles: [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO] },
    { name: "Estimates", icon: FileSpreadsheet, path: "/estimates", roles: [ROLES.CEO, ROLES.ACEO, ROLES.GM] },
    { name: "Quotation", icon: FileSignature, path: "/quotation", roles: [ROLES.CEO, ROLES.ACEO, ROLES.GM] },
    { name: "Contracts", icon: ScrollText, path: "/contracts", roles: [ROLES.CEO, ROLES.ACEO, ROLES.GM] },
    { name: "Official Letters", icon: FileText, path: "/official-letters", roles: [ROLES.CEO] },
  ]},
  { label: "Administration", items: [
    { name: "User Approvals", icon: UserCheck, path: "/users", roles: [ROLES.CEO] },
    { name: "Audit Trail", icon: ShieldCheck, path: "/audit-trail", roles: [ROLES.CEO] },
    { name: "Role Audit", icon: ShieldCheck, path: "/role-audit", roles: [ROLES.CEO] },
    { name: "SMS Logs", icon: MessageSquare, path: "/sms-logs", roles: [ROLES.CEO] },
    { name: "Settings", icon: SettingsIcon, path: "/settings", roles: [ROLES.CEO] },
  ]},
];

export default function Layout({ children }) {
  const { user, logout, isRole } = useAuth();
  const { tick } = useSync();
  const { theme, toggleTheme } = useTheme();
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [draftReceipts, setDraftReceipts] = useState(0);
  const prevPendingRef = useRef(null);
  const prevUsersRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [navOpen, setNavOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  useEffect(() => {
    const active = NAV_GROUPS.find((g) => g.items.some((i) => location.pathname.startsWith(i.path)));
    setOpenGroup(active ? active.label : null);
  }, [location.pathname]);
  const [companies, setCompanies] = useState([]);
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/companies").then(({ data }) => {
      setCompanies(data);
      const active = localStorage.getItem("stems_company");
      const c = data.find((x) => x.id === active) || data[0];
      if (c) setLogoUrl(c.logo_url || "");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (![ROLES.CEO, ROLES.ACEO, ROLES.FM, ROLES.GM].includes(user.role)) return;
    api.get("/requests/summary").then(({ data }) => {
      setPendingRequests(user.role === ROLES.FM ? (data.to_execute || 0) : (data.pending || 0));
    }).catch(() => {});
  }, [tick, user.role]);

  useEffect(() => {
    if (![ROLES.CEO, ROLES.ACEO].includes(user.role)) return;
    api.get("/approvals/summary").then(({ data }) => {
      const p = data.pending || 0;
      setPendingApprovals(p);
      const prev = prevPendingRef.current;
      if (user.role === ROLES.CEO && prev !== null && p > prev) {
        const delta = p - prev;
        toast.info(`${delta} new approval request${delta > 1 ? "s" : ""} awaiting your decision`, {
          action: { label: "Review", onClick: () => navigate("/approvals") },
        });
      }
      prevPendingRef.current = p;

      if (user.role === ROLES.CEO) {
        const pu = data.pending_users || 0;
        const prevU = prevUsersRef.current;
        if (prevU !== null && pu > prevU) {
          const d = pu - prevU;
          toast.info(`${d} new account registration${d > 1 ? "s" : ""} awaiting approval`, {
            action: { label: "Review", onClick: () => navigate("/users") },
          });
        }
        prevUsersRef.current = pu;
      }
    }).catch(() => {});
  }, [user.role, tick, navigate]);

  useEffect(() => {
    if (![ROLES.CEO, ROLES.FM].includes(user.role)) return;
    api.get("/receipts").then(({ data }) => {
      setDraftReceipts((data || []).filter((r) => r.status === "draft").length);
    }).catch(() => {});
  }, [tick, user.role]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const active = localStorage.getItem("stems_company");
    const target = companies.find((x) => x.id === active) || companies[0];
    if (!target) { toast.error("No business unit found to attach the logo to."); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/companies/${target.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(data.logo_url);
      toast.success("Logo updated");
    } catch { toast.error("Could not update the logo. Please use a PNG or JPG image."); }
    setUploading(false);
  };

  const initials = (user.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/search?q=${encodeURIComponent(q.trim())}`); setNavOpen(false); }
  };

  // Live search: navigate to results as the user types (debounced).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) return;
    const t = setTimeout(() => {
      navigate(`/search?q=${encodeURIComponent(term)}`, { replace: location.pathname === "/search" });
    }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile overlay */}
      {navOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" data-testid="nav-overlay" onClick={() => setNavOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 bottom-0 w-64 bg-[#0B132B] z-50 flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${
          navOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-800/70">
          {isRole(ROLES.CEO) ? (
            <label data-testid="nav-logo-upload" title="Click to change the logo" className="relative group h-9 w-9 rounded-lg overflow-hidden cursor-pointer shrink-0">
              <LogoImg logoUrl={logoUrl} />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Camera className="h-4 w-4 text-white" />
              </div>
              <input type="file" accept="image/*" className="hidden" data-testid="nav-logo-input" onChange={uploadLogo} disabled={uploading} />
            </label>
          ) : (
            <div className="h-9 w-9 rounded-lg overflow-hidden shrink-0" data-testid="nav-logo"><LogoImg logoUrl={logoUrl} /></div>
          )}
          <div className="leading-tight flex-1 min-w-0">
            <div className="font-heading font-bold text-orange-500 text-xl truncate">SilasTech</div>
            <div className="text-[10px] tracking-wider text-sky-300/80 uppercase">{uploading ? "Uploading…" : "STEMS"}</div>
          </div>
          <button className="lg:hidden text-slate-400 hover:text-white" data-testid="nav-close" onClick={() => setNavOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto stems-scroll py-3 px-3">
          {NAV_GROUPS.map((group) => {
            const gItems = group.items.filter((n) => n.roles.includes(user.role));
            if (!gItems.length) return null;
            const open = openGroup === group.label;
            const groupPending = (group.label === "Overview" ? pendingApprovals : 0) + (group.label === "Finance" ? pendingRequests + draftReceipts : 0);
            return (
              <div key={group.label} className="mb-1">
                <button
                  type="button"
                  onClick={() => setOpenGroup(open ? null : group.label)}
                  data-testid={`nav-group-${group.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="nav-section w-full flex items-center justify-between gap-2 hover:text-slate-300 transition-colors"
                >
                  <span>{group.label}</span>
                  <span className="flex items-center gap-1.5">
                    {!open && groupPending > 0 && (
                      <span className="h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center">{groupPending}</span>
                    )}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
                  </span>
                </button>
                {open && (
                <div className="space-y-1 mt-1">
                  {gItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setNavOpen(false)}
                        data-testid={`nav-${item.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        className={({ isActive }) =>
                          `nav-item flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium ${
                            isActive
                              ? "nav-item-active bg-[#1C2541] text-sky-400"
                              : "text-slate-300 hover:bg-[#1C2541]/60 hover:text-white"
                          }`
                        }
                      >
                        <Icon className="shrink-0" style={{ width: 18, height: 18 }} />
                        <span>{item.path === "/employees" && [ROLES.CEO, ROLES.FM].includes(user.role) ? "Administration" : item.path === "/staff" ? "Employees" : item.name}</span>
                        {item.path === "/approvals" && pendingApprovals > 0 && (
                          <span data-testid="nav-approvals-badge" className="ml-auto h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
                            {pendingApprovals}
                          </span>
                        )}
                        {item.path === "/requests" && pendingRequests > 0 && (
                          <span data-testid="nav-requests-badge" className="ml-auto h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
                            {pendingRequests}
                          </span>
                        )}
                        {item.path === "/receipts" && draftReceipts > 0 && (
                          <span data-testid="nav-receipts-draft-badge" title="Unsent draft receipts" className="ml-auto h-5 min-w-[20px] px-1.5 rounded-full bg-slate-500 text-[10px] font-bold text-white flex items-center justify-center">
                            {draftReceipts}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
        </nav>
        <SidebarOrg />
      </aside>

      {/* Navbar */}
      <header className="fixed top-0 left-0 lg:left-64 right-0 h-16 bg-[#0B132B] border-b border-slate-800 z-30 flex items-center justify-between px-3 sm:px-6 gap-2 sm:gap-4">
        <button
          className="lg:hidden text-slate-300 hover:text-white shrink-0 p-1"
          data-testid="nav-toggle"
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="hidden lg:block font-heading font-semibold text-sky-300 text-sm shrink-0">
          Enterprise Management System
        </div>
        <form onSubmit={submitSearch} className="hidden sm:block flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects, employees, transactions…"
              className="w-full h-9 pl-9 pr-3 rounded-lg bg-[#1C2541] text-sm text-white placeholder:text-slate-500 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500/60"
            />
          </div>
        </form>
        <div className="flex items-center gap-2 sm:gap-3 ml-auto sm:ml-0">
          <button
            className="sm:hidden text-slate-300 hover:text-white p-1"
            data-testid="mobile-search-btn"
            onClick={() => navigate("/search")}
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </button>
          <CompanySwitcher />
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle dark mode"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="h-9 inline-flex items-center gap-2 rounded-lg px-2.5 sm:px-3 text-slate-300 hover:text-white hover:bg-[#1C2541] border border-slate-700 transition-colors"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            <span className="hidden sm:inline text-sm font-medium">{theme === "dark" ? "Light" : "Dark"}</span>
          </button>
          <NotificationBell />
          <span className="hidden md:inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold bg-sky-500/15 text-sky-300 border border-sky-500/30">
            {user.role}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 text-white">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-sky-600 flex items-center justify-center text-xs font-bold shrink-0">
                  {initials}
                </div>
                <ChevronDown className="hidden sm:block h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <div className="text-sm font-semibold text-slate-900">{user.name}</div>
                <div className="text-xs text-slate-500">{user.email}</div>
                <div className="md:hidden mt-1 text-[11px] font-semibold text-sky-600">{user.role}</div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="theme-toggle-menu" onClick={toggleTheme}>
                {theme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                {theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        <div key={location.pathname} className="p-4 sm:p-6 lg:p-8 animate-fade">{children}</div>
      </main>
    </div>
  );
}

function LogoImg({ logoUrl }) {
  if (logoUrl) return <img src={logoUrl} alt="Company logo" className="h-full w-full object-cover" data-testid="nav-logo-image" />;
  return (
    <div className="h-full w-full bg-gradient-to-br from-sky-400 to-blue-700 flex items-center justify-center font-heading font-extrabold text-white text-sm">
      ST
    </div>
  );
}
